import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Trash2, Plus, Eye, Loader2, Mail } from "lucide-react";
import { adminGet, adminPut, isLoggedIn } from "../lib/adminAuth";
import { brand } from "../data/mock";

function Input({ label, value, onChange, type = "text", placeholder, className = "", step, min }) {
  return (
    <div className={className}>
      <label className="kicker">{label}</label>
      <input
        type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} step={step} min={min}
        className="mt-2 w-full bg-transparent border-b border-[hsl(var(--line))] focus:border-[hsl(var(--ink))] outline-none py-2 text-base font-display"
      />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="bg-[hsl(var(--bg))] border border-[hsl(var(--line))] p-6">
      <div className="kicker mb-5">{title}</div>
      {children}
    </section>
  );
}

function computeTotals(q) {
  const sub = (q.line_items || []).reduce((a, li) => a + (Number(li.quantity) || 0) * (Number(li.unit_price) || 0), 0);
  const disc = Number(q.discount_amount) || 0;
  const taxable = Math.max(sub - disc, 0);
  const tax = taxable * ((Number(q.tax_percent) || 0) / 100);
  const total = taxable + tax;
  const dep = total * ((Number(q.deposit_percent) || 0) / 100);
  return { subtotal: sub, discount: disc, tax, total, deposit: dep, balance: total - dep };
}

function fmt(n, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n || 0);
}

const SERVICE_OPTIONS = ["Full Design", "Planning", "Wedding Planning", "Event Management", "High-End Decoration"];

function stableKey(prefix) {
  return (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function hydrateKeys(doc) {
  if (!doc) return doc;
  return {
    ...doc,
    line_items: (doc.line_items || []).map((li) => (li._key ? li : { ...li, _key: stableKey("li") })),
    reference_images: (doc.reference_images || []).map((im) => (im._key ? im : { ...im, _key: stableKey("im") })),
  };
}

export default function QuotationEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    if (!isLoggedIn()) { nav("/admin/login"); return; }
    (async () => {
      try {
        const d = await adminGet(`/admin/quotations/${id}`);
        setQ(hydrateKeys(d));
      } catch (err) {
        if (err.message === "unauthorized") nav("/admin/login");
        else setError(err.message);
      }
    })();
  }, [id, nav]);

  const filledReferenceImages = useMemo(
    () => (q?.reference_images || []).filter((im) => im.url),
    [q]
  );

  if (!q) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--bg-alt))]">
        {error ? <div className="text-[hsl(var(--accent))]">{error}</div> : <Loader2 className="animate-spin" />}
      </div>
    );
  }

  const update = (patch) => setQ((prev) => ({ ...prev, ...patch }));
  const updateClient = (patch) => update({ client: { ...q.client, ...patch } });
  const updateEvent = (patch) => update({ event: { ...q.event, ...patch } });

  const addLine = () => update({ line_items: [...(q.line_items || []), { _key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `li-${Date.now()}-${Math.random()}`, description: "", quantity: 1, unit_price: 0 }] });
  const setLine = (i, patch) => {
    const li = [...q.line_items];
    li[i] = { ...li[i], ...patch };
    update({ line_items: li });
  };
  const delLine = (i) => update({ line_items: q.line_items.filter((_, idx) => idx !== i) });

  const addImage = () => update({ reference_images: [...(q.reference_images || []), { _key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `im-${Date.now()}-${Math.random()}`, url: "", caption: "" }] });
  const setImage = (i, patch) => {
    const im = [...q.reference_images];
    im[i] = { ...im[i], ...patch };
    update({ reference_images: im });
  };
  const delImage = (i) => update({ reference_images: q.reference_images.filter((_, idx) => idx !== i) });

  const toggleService = (svc) => {
    const set = new Set(q.services || []);
    if (set.has(svc)) set.delete(svc); else set.add(svc);
    update({ services: [...set] });
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        client: q.client, event: q.event, services: q.services,
        reference_images: q.reference_images, line_items: q.line_items,
        currency: q.currency, discount_amount: Number(q.discount_amount) || 0,
        tax_percent: Number(q.tax_percent) || 0, deposit_percent: Number(q.deposit_percent) || 0,
        terms: q.terms, status: q.status,
      };
      const updated = await adminPut(`/admin/quotations/${id}`, payload);
      setQ(hydrateKeys(updated));
      setSavedAt(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const totals = computeTotals(q);
  const currency = q.currency || "USD";

  return (
    <div className="min-h-screen bg-[hsl(var(--bg-alt))] text-[hsl(var(--ink))]">
      <header className="bg-[hsl(var(--bg))] border-b border-[hsl(var(--line))] sticky top-0 z-10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/quotations" className="text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--accent))]"><ArrowLeft size={18} /></Link>
            <img src="https://customer-assets-39nsmqrw.emergentagent.net/job_elegant-event-design-1/artifacts/v1a0y2ji_Shobhawithshubhra-logo-final.png" alt="SwS" className="h-10 w-auto object-contain" />
            <div className="font-editorial text-xl tracking-wider">{q.number}</div>
          </div>
          <div className="flex items-center gap-4">
            {savedAt && <span className="text-xs text-[hsl(var(--ink-soft))]">Saved {savedAt.toLocaleTimeString()}</span>}
            <a
              href={(() => {
                const to = q.client?.email || "";
                const subject = `Your quotation from Shobha with Shubhra — ${q.number}`;
                const first = (q.client?.name || "there").split(" ")[0];
                const evt = q.event?.event_type ? `${q.event.event_type}${q.event?.date ? ` on ${new Date(q.event.date).toLocaleDateString()}` : ""}` : "your celebration";
                const currency = q.currency || "USD";
                const total = new Intl.NumberFormat("en-US", { style: "currency", currency }).format(totals.total || 0);
                const dep = new Intl.NumberFormat("en-US", { style: "currency", currency }).format(totals.deposit || 0);
                const body = [
                  `Dear ${first},`, "",
                  `Thank you so much for the opportunity to design ${evt}. It would be a true joy to bring your vision to life.`, "",
                  `Please find your personalised quotation (${q.number}) attached to this email.`,
                  `Total investment: ${total}`,
                  `Deposit to confirm booking: ${dep}`, "",
                  "I'd love to walk you through every detail on a call — please reply with a time that suits you.", "",
                  "Warm regards,", "Shubhra Prasad", "Shobha with Shubhra", `${brand.email} · ${brand.phone}`, brand.location,
                ].join("\n");
                return `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
              })()}
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase link-underline"
              title={q.client?.email ? `Email to ${q.client.email}` : "No client email set"}
            >
              <Mail size={14} /> Email
            </a>
            <Link to={`/admin/quotations/${id}/print`} target="_blank" className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase link-underline">
              <Eye size={14} /> Preview & Print
            </Link>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 border border-[hsl(var(--ink))] hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--bg))] px-4 py-2 text-[11px] tracking-[0.3em] uppercase transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 md:px-10 py-8 space-y-6">
        {error && <div className="border border-[hsl(var(--accent))] text-[hsl(var(--accent))] p-4 text-sm">{error}</div>}

        <div className="grid md:grid-cols-2 gap-6">
          <Section title="Client information">
            <div className="grid grid-cols-1 gap-4">
              <Input label="Full name" value={q.client?.name} onChange={(v) => updateClient({ name: v })} />
              <Input label="Email" type="email" value={q.client?.email} onChange={(v) => updateClient({ email: v })} />
              <Input label="Phone" value={q.client?.phone} onChange={(v) => updateClient({ phone: v })} />
              <Input label="Address" value={q.client?.address} onChange={(v) => updateClient({ address: v })} />
            </div>
          </Section>

          <Section title="Event details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Event type" placeholder="Wedding, Sangeet, Baby Shower..." value={q.event?.event_type} onChange={(v) => updateEvent({ event_type: v })} />
              <Input label="Event date" type="date" value={q.event?.date} onChange={(v) => updateEvent({ date: v })} />
              <Input label="Venue" value={q.event?.venue} onChange={(v) => updateEvent({ venue: v })} />
              <Input label="City" value={q.event?.city} onChange={(v) => updateEvent({ city: v })} />
              <Input label="Guest count" value={q.event?.guests} onChange={(v) => updateEvent({ guests: v })} />
              <Input label="Status" value={q.status} onChange={(v) => update({ status: v })} placeholder="draft, sent, accepted, declined" />
              <div className="md:col-span-2">
                <label className="kicker">Notes / requirements</label>
                <textarea rows={3} value={q.event?.notes || ""} onChange={(e) => updateEvent({ notes: e.target.value })} className="mt-2 w-full bg-transparent border-b border-[hsl(var(--line))] focus:border-[hsl(var(--ink))] outline-none py-2 text-base font-display resize-none" />
              </div>
            </div>
          </Section>
        </div>

        <Section title="Services requested">
          <div className="flex flex-wrap gap-2">
            {SERVICE_OPTIONS.map((s) => {
              const on = (q.services || []).includes(s);
              return (
                <button key={s} onClick={() => toggleService(s)} className={`text-xs tracking-[0.2em] uppercase border px-3 py-2 ${on ? "bg-[hsl(var(--ink))] text-[hsl(var(--bg))] border-[hsl(var(--ink))]" : "border-[hsl(var(--line))] hover:border-[hsl(var(--ink))]"}`}>{s}</button>
              );
            })}
          </div>
        </Section>

        <Section title={`Line items (${(q.line_items || []).length})`}>
          <div className="space-y-3">
            {(q.line_items || []).map((li, i) => (
              <div key={li._key || `line-${i}`} className="grid grid-cols-12 gap-3 items-end">
                <div className="col-span-6"><Input label={i === 0 ? "Description" : ""} value={li.description} onChange={(v) => setLine(i, { description: v })} /></div>
                <div className="col-span-2"><Input label={i === 0 ? "Qty" : ""} type="number" step="0.01" min="0" value={li.quantity} onChange={(v) => setLine(i, { quantity: Number(v) })} /></div>
                <div className="col-span-3"><Input label={i === 0 ? "Unit price" : ""} type="number" step="0.01" min="0" value={li.unit_price} onChange={(v) => setLine(i, { unit_price: Number(v) })} /></div>
                <div className="col-span-1 flex justify-end pb-2">
                  <button onClick={() => delLine(i)} className="text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--accent))]"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
            <button onClick={addLine} className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase link-underline"><Plus size={14} /> Add item</button>
          </div>
        </Section>

        <div className="grid md:grid-cols-2 gap-6">
          <Section title="Reference photos">
            <p className="text-xs text-[hsl(var(--ink-soft))] mb-4">Paste image URLs (imgur, Google Drive public share links, any web-accessible image).</p>
            <div className="space-y-3">
              {(q.reference_images || []).map((im, i) => (
                <div key={im._key || `ref-${i}`} className="grid grid-cols-12 gap-3 items-end">
                  <div className="col-span-7"><Input label={i === 0 ? "Image URL" : ""} value={im.url} onChange={(v) => setImage(i, { url: v })} placeholder="https://..." /></div>
                  <div className="col-span-4"><Input label={i === 0 ? "Caption" : ""} value={im.caption} onChange={(v) => setImage(i, { caption: v })} /></div>
                  <div className="col-span-1 flex justify-end pb-2"><button onClick={() => delImage(i)} className="text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--accent))]"><Trash2 size={14} /></button></div>
                </div>
              ))}
              <button onClick={addImage} className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase link-underline"><Plus size={14} /> Add photo</button>
            </div>
            {q.reference_images && q.reference_images.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mt-6">
                {filledReferenceImages.map((im, i) => (
                  <div key={im._key || `preview-${i}`} className="aspect-square bg-[hsl(var(--bg-alt))] overflow-hidden">
                    <img src={im.url} alt={im.caption} className="w-full h-full object-cover" onError={(e) => { e.target.style.opacity = 0.3; }} />
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title="Pricing & terms">
            <div className="grid grid-cols-1 gap-4">
              <Input label="Currency" value={q.currency} onChange={(v) => update({ currency: v })} placeholder="USD" />
              <Input label="Discount amount" type="number" step="0.01" min="0" value={q.discount_amount} onChange={(v) => update({ discount_amount: Number(v) })} />
              <Input label="Tax %" type="number" step="0.01" min="0" value={q.tax_percent} onChange={(v) => update({ tax_percent: Number(v) })} />
              <Input label="Deposit %" type="number" step="0.01" min="0" value={q.deposit_percent} onChange={(v) => update({ deposit_percent: Number(v) })} />
              <div>
                <label className="kicker">Terms & conditions</label>
                <textarea rows={4} value={q.terms || ""} onChange={(e) => update({ terms: e.target.value })} className="mt-2 w-full bg-transparent border-b border-[hsl(var(--line))] focus:border-[hsl(var(--ink))] outline-none py-2 text-sm font-display resize-none" />
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-[hsl(var(--line))] space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[hsl(var(--ink-soft))]">Subtotal</span><span>{fmt(totals.subtotal, currency)}</span></div>
              <div className="flex justify-between"><span className="text-[hsl(var(--ink-soft))]">Discount</span><span>− {fmt(totals.discount, currency)}</span></div>
              <div className="flex justify-between"><span className="text-[hsl(var(--ink-soft))]">Tax</span><span>{fmt(totals.tax, currency)}</span></div>
              <div className="flex justify-between font-display text-2xl pt-2 border-t border-[hsl(var(--line))]"><span>Total</span><span>{fmt(totals.total, currency)}</span></div>
              <div className="flex justify-between text-[hsl(var(--accent))]"><span>Deposit due</span><span>{fmt(totals.deposit, currency)}</span></div>
              <div className="flex justify-between text-[hsl(var(--ink-soft))]"><span>Balance</span><span>{fmt(totals.balance, currency)}</span></div>
            </div>
          </Section>
        </div>
      </main>
    </div>
  );
}
